import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, Tooltip, Chip } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";

const PatientOrdersCollectPanel = ({
  t,
  patientOrders = [],
  selectedOrderId,
  onOrderSelect,
  DEVICE_TYPE,
  unprocessedOrdersCount = 0,
  orderCompletionStatus = {},
}) => {
  const unprocessedOrders = (patientOrders || []).filter((order) => {
    if (!order) return false;
    const typeId = order.type_id;
    return (
      typeId !== DEVICE_TYPE.Completed &&
      typeId !== DEVICE_TYPE.Returned &&
      typeId !== DEVICE_TYPE.Cancelled
    );
  });

  if (!unprocessedOrders || unprocessedOrders.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        width: "auto",
      }}
    >
      {/* Header label showing order count */}
      <Tooltip
        title={
          unprocessedOrdersCount === 1
            ? t("Console.ActionPopup.SingleOrderTooltip", {
                defaultValue: "Current order. Click to view details.",
              })
            : t("Console.ActionPopup.MultipleOrdersTooltip", {
                defaultValue: `${unprocessedOrdersCount} orders available. Click any order below to switch.`,
                count: unprocessedOrdersCount,
              })
        }
        arrow
        placement="left"
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
            px: 1.5,
            py: 1,
            mb: 1,
            borderRadius: "8px 8px 0 0",
            background: "linear-gradient(135deg, rgba(245, 158, 11, 0.95) 0%, rgba(217, 119, 6, 0.9) 100%)",
            border: "2px solid #f59e0b",
            borderBottom: "2px solid #f59e0b",
            borderTop: "none",
            boxShadow: "0 2px 8px rgba(245, 158, 11, 0.3)",
            cursor: "help",
            flexShrink: 0,
            width: "85px",
          }}
        >
          <InfoIcon
            sx={{
              fontSize: 14,
              color: "#ffffff",
            }}
          />
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 700,
              color: "#ffffff",
              letterSpacing: "0.02em",
              textAlign: "center",
              textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
              lineHeight: 1.2,
            }}
          >
            {unprocessedOrdersCount} {t("Console.ActionPopup.OrdersAvailable", {
              defaultValue: unprocessedOrdersCount === 1 ? "Order" : "Orders",
            })}
          </Typography>
        </Box>
      </Tooltip>

      {/* Order cards container */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          paddingY: 2,
          paddingX: 0,
          overflowY: "auto",
          overflowX: "hidden",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#f59e0b",
            borderRadius: "3px",
            "&:hover": {
              background: "#d97706",
            },
          },
        }}
      >
      {unprocessedOrders.map((order, index) => {
        if (!order) return null;

        const isSelected = order.id === selectedOrderId;
        const storageLocation = order.storage_location || "N/A";
        const completionStatus = orderCompletionStatus[order.id] || null;
        const isCompleted = completionStatus && completionStatus.isFullyComplete === true;

        return (
          <Box
            key={order.id || index}
            onClick={() => onOrderSelect && onOrderSelect(order)}
            sx={{
              position: "relative",
              width: "85px",
              minWidth: "85px",
              minHeight: "105px",
              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              flexShrink: 0,
              "&:hover": {
                transform: "translateX(-4px)",
              },
            }}
          >
            {(
              <Box
                sx={{
                  position: "absolute",
                  left: "-8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 0,
                  height: 0,
                  borderTop: "8px solid transparent",
                  borderBottom: "8px solid transparent",
                  borderRight: isSelected
                    ? "8px solid #f59e0b"
                    : "8px solid #ffffff",
                  zIndex: 2,
                  filter: isSelected
                    ? "drop-shadow(-2px 0 4px rgba(245, 158, 11, 0.4))"
                    : "drop-shadow(-2px 0 4px rgba(0, 0, 0, 0.1))",
                }}
              />
            )}

            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                background: isCompleted
                  ? isSelected
                    ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                    : "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)"
                  : isSelected
                    ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                    : "#fffbeb",
                border: isCompleted
                  ? isSelected
                    ? "2px solid #10b981"
                    : "2px solid #10b981"
                  : isSelected
                    ? "2px solid #f59e0b"
                    : "2px solid #f59e0b",
                borderRight: "none",
                borderLeft: "none",
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                borderTopRightRadius: "12px",
                borderBottomRightRadius: "12px",
                boxShadow: isCompleted
                  ? isSelected
                    ? "0 4px 16px rgba(16, 185, 129, 0.4), -2px 0 10px rgba(16, 185, 129, 0.3), 2px 0 4px rgba(0, 0, 0, 0.05)"
                    : "0 2px 8px rgba(16, 185, 129, 0.2), -2px 0 6px rgba(16, 185, 129, 0.15), 2px 0 4px rgba(0, 0, 0, 0.05)"
                  : isSelected
                    ? "0 4px 16px rgba(245, 158, 11, 0.4), -2px 0 10px rgba(245, 158, 11, 0.3), 2px 0 4px rgba(0, 0, 0, 0.05)"
                    : "0 2px 8px rgba(245, 158, 11, 0.2), -2px 0 6px rgba(245, 158, 11, 0.15), 2px 0 4px rgba(0, 0, 0, 0.05)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: 1.5,
                paddingRight: 2,
                paddingLeft: 1.5,
                color: isCompleted
                  ? isSelected
                    ? "#ffffff"
                    : "#065f46"
                  : isSelected
                    ? "#ffffff"
                    : "#1e293b",
                "&:hover": {
                  boxShadow: isCompleted
                    ? isSelected
                      ? "0 6px 20px rgba(16, 185, 129, 0.5), -4px 0 14px rgba(16, 185, 129, 0.35), 2px 0 6px rgba(0, 0, 0, 0.08)"
                      : "0 4px 12px rgba(16, 185, 129, 0.3), -4px 0 10px rgba(16, 185, 129, 0.2), 2px 0 6px rgba(0, 0, 0, 0.08)"
                    : isSelected
                      ? "0 6px 20px rgba(245, 158, 11, 0.5), -4px 0 14px rgba(245, 158, 11, 0.35), 2px 0 6px rgba(0, 0, 0, 0.08)"
                      : "0 4px 12px rgba(245, 158, 11, 0.3), -4px 0 10px rgba(245, 158, 11, 0.2), 2px 0 6px rgba(0, 0, 0, 0.08)",
                  borderColor: isCompleted
                    ? isSelected
                      ? "#059669"
                      : "#059669"
                    : isSelected
                      ? "#d97706"
                      : "#d97706",
                  transform: "scale(1.02)",
                  background: isCompleted
                    ? isSelected
                      ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                      : "linear-gradient(135deg, #a7f3d0 0%, #6ee7b7 100%)"
                    : isSelected
                      ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                      : "#fef3c7",
                },
              }}
            >
              {/* Collect text and icon on same line */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.5,
                  mb: 1,
                  width: "100%",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    color: isCompleted
                      ? isSelected
                        ? "rgba(255, 255, 255, 0.95)"
                        : "#065f46"
                      : isSelected
                        ? "rgba(255, 255, 255, 0.95)"
                        : "#92400e",
                    textAlign: "center",
                    lineHeight: 1.2,
                  }}
                >
                  {t("Console.ActionPopup.Collect", { defaultValue: "Collect" })}
                </Typography>
                {isSelected && (
                  <CheckCircleIcon
                    sx={{
                      fontSize: 18,
                      color: isCompleted
                        ? isSelected
                          ? "#ffffff"
                          : "#065f46"
                        : isSelected
                          ? "#ffffff"
                          : "#92400e",
                      filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))",
                      flexShrink: 0,
                    }}
                  />
                )}
              </Box>

              <Typography
                sx={{
                  fontSize: "0.95rem",
                  fontWeight: 800,
                  color: isCompleted
                    ? isSelected
                      ? "#ffffff"
                      : "#065f46"
                    : isSelected
                      ? "#ffffff"
                      : "#92400e",
                  textAlign: "center",
                  lineHeight: 1.2,
                  wordBreak: "break-word",
                  maxWidth: "100%",
                  letterSpacing: "0.02em",
                  mb: isCompleted ? 1 : 0,
                }}
              >
                {storageLocation !== "N/A" ? storageLocation : "N/A"}
              </Typography>

              {/* Completed Chip - shown under the storage location */}
              {isCompleted && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      mt: 0.5,
                    }}
                  >
                  <Chip
                    icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                    label={t("Console.ActionPopup.Completed", { defaultValue: "Completed" })}
                    size="small"
                    sx={{
                      height: "22px",
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      backgroundColor: isSelected ? "rgba(255, 255, 255, 0.25)" : "#10b981",
                      color: isSelected ? "#ffffff" : "#ffffff",
                      border: isSelected ? "1px solid rgba(255, 255, 255, 0.4)" : "none",
                      "& .MuiChip-icon": {
                        color: isSelected ? "#ffffff" : "#ffffff",
                        fontSize: "14px",
                      },
                      boxShadow: isSelected 
                        ? "0 2px 4px rgba(0, 0, 0, 0.2)" 
                        : "0 2px 4px rgba(16, 185, 129, 0.3)",
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
      </Box>
    </Box>
  );
};

PatientOrdersCollectPanel.propTypes = {
  t: PropTypes.func.isRequired,
  patientOrders: PropTypes.array,
  selectedOrderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onOrderSelect: PropTypes.func.isRequired,
  DEVICE_TYPE: PropTypes.object.isRequired,
  unprocessedOrdersCount: PropTypes.number,
  orderCompletionStatus: PropTypes.object,
};

PatientOrdersCollectPanel.defaultProps = {
  patientOrders: [],
  selectedOrderId: null,
  orderCompletionStatus: {},
};

export default PatientOrdersCollectPanel;



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/components/PatientOrdersCollectPanel.jsx